"use client";
import { createContext, useContext, useEffect, useState } from "react";
import type { ReaderKey } from "@/app/(main)/buy-now/_utils/products";
import type { DiscountValidationResponse } from "@/lib/types/discount";
import { STARTER_KIT_PRICE } from "@/lib/utils/common/constants";
import { getFallbackDiscountCode } from "@/lib/utils/shopify";

type WorkflowPriceState = {
  originalPrice: number;
  discountAmount: number;
  finalPrice: number;
};

function getWorkflowPriceState(
  workflow: ReaderKey,
  discountAmount: number,
): WorkflowPriceState {
  const originalPrice = STARTER_KIT_PRICE[workflow];
  return {
    originalPrice,
    discountAmount,
    finalPrice: Math.max(0, originalPrice - discountAmount),
  };
}

const DiscountContext = createContext<{
  discount: string;
  discounts: Record<ReaderKey, string>;
  loadingDiscounts: Record<ReaderKey, boolean>;
  pricing: Record<ReaderKey, WorkflowPriceState>;
  isNgOutOfStock: boolean;
}>({
  discount: "",
  discounts: { og: "", ng: "" },
  loadingDiscounts: { og: false, ng: false },
  pricing: {
    og: getWorkflowPriceState("og", getFallbackDiscountCode("og").value),
    ng: getWorkflowPriceState("ng", getFallbackDiscountCode("ng").value),
  },
  isNgOutOfStock: false,
});

export const DiscountProvider = ({
  children,
  discounts,
  fallbackDiscounts,
  isNgOutOfStock = false,
  validationPromises,
  isNgOutOfStockPromise,
}: {
  children: React.ReactNode;
  discounts: Record<ReaderKey, string>;
  fallbackDiscounts: Record<ReaderKey, string>;
  isNgOutOfStock?: boolean;
  validationPromises?: Partial<
    Record<ReaderKey, Promise<DiscountValidationResponse>>
  >;
  isNgOutOfStockPromise?: Promise<boolean>;
}) => {
  const [resolvedDiscounts, setResolvedDiscounts] = useState(discounts);
  const [loadingDiscounts, setLoadingDiscounts] = useState<Record<ReaderKey, boolean>>({
    og: Boolean(validationPromises?.og),
    ng: Boolean(validationPromises?.ng),
  });
  const [pricing, setPricing] = useState<Record<ReaderKey, WorkflowPriceState>>({
    og: getWorkflowPriceState("og", getFallbackDiscountCode("og").value),
    ng: getWorkflowPriceState("ng", getFallbackDiscountCode("ng").value),
  });
  const [outOfStock, setOutOfStock] = useState(isNgOutOfStock);

  useEffect(() => {
    setResolvedDiscounts(discounts);
  }, [discounts]);

  useEffect(() => {
    setLoadingDiscounts({
      og: Boolean(validationPromises?.og),
      ng: Boolean(validationPromises?.ng),
    });
  }, [validationPromises]);

  useEffect(() => {
    let active = true;

    (["og", "ng"] as const).forEach((workflow) => {
      const validationPromise = validationPromises?.[workflow];
      if (!validationPromise) {
        setPricing((current) => ({
          ...current,
          [workflow]: getWorkflowPriceState(
            workflow,
            getFallbackDiscountCode(workflow).value,
          ),
        }));
        return;
      }

      validationPromise.then((result) => {
        if (!active) return;

        const discountAmount = result.valid
          ? (result.discount ?? getFallbackDiscountCode(workflow).value)
          : getFallbackDiscountCode(workflow).value;

        setResolvedDiscounts((current) => ({
          ...current,
          [workflow]: result.valid
            ? discounts[workflow]
            : fallbackDiscounts[workflow],
        }));
        setPricing((current) => ({
          ...current,
          [workflow]: getWorkflowPriceState(workflow, discountAmount),
        }));
        setLoadingDiscounts((current) => ({
          ...current,
          [workflow]: false,
        }));
      });
    });

    return () => {
      active = false;
    };
  }, [discounts, fallbackDiscounts, validationPromises]);

  useEffect(() => {
    let active = true;
    if (isNgOutOfStockPromise) {
      isNgOutOfStockPromise.then((val) => {
        if (!active) return;
        setOutOfStock(val);
      });
    }
    return () => {
      active = false;
    };
  }, [isNgOutOfStockPromise]);

  return (
    <DiscountContext.Provider
      value={{
        discount: resolvedDiscounts.ng,
        discounts: resolvedDiscounts,
        loadingDiscounts,
        pricing,
        isNgOutOfStock: outOfStock,
      }}
    >
      {children}
    </DiscountContext.Provider>
  );
};

export const useDiscount = () => {
  const context = useContext(DiscountContext);
  if (!context) {
    throw new Error("useDiscount must be used within a DiscountProvider");
  }
  return context;
};
